/**
 * QUIPWA — DriveHttp
 *
 * The only place in the app that calls `fetch` against Google APIs. It adds the
 * bearer token, turns every failure into a typed `DriveError`, and retries the
 * failures that are worth retrying (429, 5xx, dropped connections) with
 * exponential backoff and full jitter, honouring `Retry-After`.
 *
 * Everything time- or environment-dependent (fetch, sleep, randomness, online
 * status) is injected, so the retry policy is unit-tested without real waits.
 *
 * ```ts
 * const http = new DriveHttp({ getAccessToken: () => auth.getAccessToken() })
 * const meta = await http.json<DriveFile>(`${DRIVE_API}/files/${id}?fields=id,version`)
 * ```
 */

import { DriveError, classifyHttpError, classifyNetworkError } from './drive-errors.ts'

export interface RetryPolicy {
  /** Total attempts including the first one. */
  maxAttempts: number
  /** Delay before the first retry; doubles each attempt. */
  baseDelayMs: number
  /** Upper bound for a single backoff delay. */
  maxDelayMs: number
}

export const DEFAULT_RETRY_POLICY: RetryPolicy = {
  maxAttempts: 4,
  baseDelayMs: 500,
  maxDelayMs: 8_000,
}

export interface DriveHttpOptions {
  /** Returns a valid access token, or null when there is none. */
  getAccessToken: () => Promise<string | null>
  /** Called once per 401 so the auth layer can mark the session as expired. */
  onUnauthorized?: () => void
  /** Called when Google says the token lacks a scope (needs new consent). */
  onInsufficientScope?: () => void
  retry?: Partial<RetryPolicy>
  fetch?: typeof fetch
  sleep?: (ms: number) => Promise<void>
  random?: () => number
  isOnline?: () => boolean
}

export interface DriveRequestInit extends Omit<RequestInit, 'headers'> {
  headers?: Record<string, string>
  /** Short label used in error messages, e.g. `files.get`. Defaults to method + path. */
  label?: string
  /** Disable retries for non-idempotent requests that must not run twice. */
  retry?: boolean
}

const defaultSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

/**
 * Backoff delay for a given retry attempt (1-based), "full jitter" variant:
 * a uniformly random delay in [0, min(max, base · 2^(attempt-1))].
 * Full jitter spreads retries from many clients/requests instead of letting
 * them collide again on the same instant.
 */
export function backoffDelay(attempt: number, policy: RetryPolicy, random: () => number): number {
  const ceiling = Math.min(policy.maxDelayMs, policy.baseDelayMs * 2 ** (attempt - 1))
  return Math.round(random() * ceiling)
}

export class DriveHttp {
  private readonly getAccessToken: () => Promise<string | null>
  private readonly onUnauthorized: (() => void) | undefined
  private readonly onInsufficientScope: (() => void) | undefined
  private readonly policy: RetryPolicy
  private readonly fetchImpl: typeof fetch
  private readonly sleep: (ms: number) => Promise<void>
  private readonly random: () => number
  private readonly isOnline: () => boolean

  constructor(options: DriveHttpOptions) {
    this.getAccessToken = options.getAccessToken
    this.onUnauthorized = options.onUnauthorized
    this.onInsufficientScope = options.onInsufficientScope
    this.policy = { ...DEFAULT_RETRY_POLICY, ...options.retry }
    // Bind: calling an unbound `fetch` off an object throws "Illegal invocation".
    this.fetchImpl = options.fetch ?? ((input, init) => globalThis.fetch(input, init))
    this.sleep = options.sleep ?? defaultSleep
    this.random = options.random ?? Math.random
    this.isOnline = options.isOnline ?? (() => typeof navigator === 'undefined' || navigator.onLine !== false)
  }

  /** Perform a request and return the raw (2xx) Response. */
  async request(url: string, init: DriveRequestInit = {}): Promise<Response> {
    const label = init.label ?? `${init.method ?? 'GET'} ${new URL(url).pathname}`
    const maxAttempts = init.retry === false ? 1 : this.policy.maxAttempts

    let lastError: DriveError | null = null
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      if (attempt > 1 && lastError) {
        const serverWait = lastError.retryAfterSeconds !== null ? lastError.retryAfterSeconds * 1000 : 0
        const delay = Math.max(serverWait, backoffDelay(attempt - 1, this.policy, this.random))
        await this.sleep(Math.min(delay, this.policy.maxDelayMs * 4))
      }

      try {
        return await this.attempt(url, init, label)
      } catch (err) {
        lastError = err instanceof DriveError ? err : classifyNetworkError(err, label, this.isOnline())
        if (!lastError.retryable) throw lastError
      }
    }
    throw lastError ?? new DriveError('unknown', `Drive ${label} → sin respuesta`)
  }

  /** Request expecting a JSON body. */
  async json<T>(url: string, init: DriveRequestInit = {}): Promise<T> {
    const res = await this.request(url, init)
    return (await res.json()) as T
  }

  /** Request expecting a binary body. */
  async arrayBuffer(url: string, init: DriveRequestInit = {}): Promise<ArrayBuffer> {
    const res = await this.request(url, init)
    return res.arrayBuffer()
  }

  /** Request expecting a text body. */
  async text(url: string, init: DriveRequestInit = {}): Promise<string> {
    const res = await this.request(url, init)
    return res.text()
  }

  // ── internals ─────────────────────────────────────────────────────────────

  private async attempt(url: string, init: DriveRequestInit, label: string): Promise<Response> {
    const token = await this.getAccessToken()
    if (!token) {
      throw new DriveError('unauthorized', `Drive ${label} → no hay sesión de Google activa`)
    }

    const { headers = {}, label: _label, retry: _retry, ...rest } = init
    let res: Response
    try {
      res = await this.fetchImpl(url, {
        ...rest,
        headers: { ...headers, Authorization: `Bearer ${token}` },
      })
    } catch (err) {
      throw classifyNetworkError(err, label, this.isOnline())
    }

    if (res.ok) return res

    const body = await res.text().catch(() => '')
    const error = classifyHttpError(res.status, body, res.headers.get('Retry-After'), label)
    if (error.kind === 'unauthorized') this.onUnauthorized?.()
    if (error.kind === 'insufficient_scope') this.onInsufficientScope?.()
    throw error
  }
}
